import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bailAccountApi } from "../../../modules/commissions/bailaccount/api";
import {
  BailAccountMovementFilterPageRequest,
  BailAccountsReportFilterExportRequest,
  BailAccountsReportFilterPageRequest,
  CreateUpdateBailAccountMovement,
  CreateUpdateBailAccountSettings,
} from "../../../modules/commissions/bailaccount/types";
import { UUID } from "../../../typings/global";
import { EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";
import { COMMISSIONS_SETTINGS_QUERY_KEYS } from "./commissions/queryKeys";

export const BAIL_ACCOUNT_QUERY_KEYS = {
  getBailAccountsReport: (payload?: BailAccountsReportFilterPageRequest) =>
    payload ? ["getBailAccountsReport", payload] : ["getBailAccountsReport"],
  downloadBailAccountsExport: () => ["downloadBailAccountsExport"],
  createBailAccountSettings: () => ["createBailAccountSettings"],
  updateBailAccountSettings: () => ["updateBailAccountSettings"],
  deleteBailAccountSettings: () => ["deleteBailAccountSettings"],
  filterBailAccountMovements: (payload?: EntityObject<BailAccountMovementFilterPageRequest>) =>
    payload ? ["filterBailAccountMovements", payload] : ["filterBailAccountMovements"],
  createBailAccountMovement: () => ["createBailAccountMovement"],
  updateBailAccountMovement: () => ["updateBailAccountMovement"],
  deleteBailAccountMovement: () => ["deleteBailAccountMovement"],
};

const invalidateAfterBailAccountMovementChange = (queryClient: QueryClient, agentId: UUID) => {
  queryClient.invalidateQueries({ queryKey: BAIL_ACCOUNT_QUERY_KEYS.filterBailAccountMovements() });
  queryClient.invalidateQueries({ queryKey: BAIL_ACCOUNT_QUERY_KEYS.getBailAccountsReport() });
  queryClient.invalidateQueries({ queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(agentId) });
  queryClient.invalidateQueries({ queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(agentId) });
};

export const useGetBailAccountsReport = (payload: BailAccountsReportFilterPageRequest) => {
  return useQuery({
    queryKey: BAIL_ACCOUNT_QUERY_KEYS.getBailAccountsReport(payload),
    queryFn: async () => {
      const { data } = await bailAccountApi.getBailAccountsReport(payload);

      return data;
    },
  });
};

export const useDownloadBailAccountsExport = () => {
  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.downloadBailAccountsExport(),
    mutationFn: async (payload: BailAccountsReportFilterExportRequest) => {
      const response = await bailAccountApi.downloadBailAccountsExport(payload);
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useCreateBailAccountSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.createBailAccountSettings(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateBailAccountSettings>) => {
      const { data } = await bailAccountApi.createBailAccountSettings(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
      });

      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateBailAccountSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.updateBailAccountSettings(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateBailAccountSettings>) => {
      const { data } = await bailAccountApi.updateBailAccountSettings(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
      });

      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteBailAccountSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.deleteBailAccountSettings(),
    mutationFn: async (id: UUID) => {
      return bailAccountApi.deleteBailAccountSettings({ id });
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload),
      });

      Notification.itemDeletedNotification();
    },
  });
};

export const useFilterBailAccountMovements = (payload: EntityObject<BailAccountMovementFilterPageRequest>) => {
  return useQuery({
    queryKey: BAIL_ACCOUNT_QUERY_KEYS.filterBailAccountMovements(payload),
    queryFn: async () => {
      const { data } = await bailAccountApi.filterBailAccountMovements(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateBailAccountMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.createBailAccountMovement(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateBailAccountMovement>) => {
      const { data } = await bailAccountApi.createBailAccountMovement(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      invalidateAfterBailAccountMovementChange(queryClient, mutatedPayload.id);
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateBailAccountMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.updateBailAccountMovement(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateBailAccountMovement>) => {
      const { data } = await bailAccountApi.updateBailAccountMovement(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      invalidateAfterBailAccountMovementChange(queryClient, mutatedPayload.id1);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteBailAccountMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: BAIL_ACCOUNT_QUERY_KEYS.deleteBailAccountMovement(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return bailAccountApi.deleteBailAccountMovement(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      invalidateAfterBailAccountMovementChange(queryClient, mutatedPayload.id1);
      Notification.itemDeletedNotification();
    },
  });
};
